import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './OffersPage.css';
import freightPic   from '../../images/offers-freight.jpg';
import warehousePic from '../../images/offers-warehouse.jpg';
import deliveryPic  from '../../images/offers-delivery.jpg';
import planPic      from '../../images/offers-plan.jpg';

const FEATURED = {
  img: freightPic,
  label: 'Most Requested',
  title: 'Freight Transportation',
  outcome: 'Coast-to-coast delivery in 48–96 hours',
  text: 'Ground and air freight for FTL and LTL shipments across the Southeast, Gulf Coast, and East Coast. Real-time tracking on every load. Proactive delay notification — we call you before your customer does.',
  bullets: ['FTL & LTL', 'Real-time tracking', 'Proactive delay alerts', 'Same-day dispatch available'],
};

const SERVICES = [
  {
    img: warehousePic, title: 'Warehouse & Storage',
    outcome: '99% order fill accuracy',
    text: 'Climate-controlled facilities in the Tampa Bay area. Inventory management, cross-docking, pick-and-pack — no third-party handoffs. Your stock is always dispatch-ready.',
    to: '/services/warehouse-storage',
  },
  {
    img: deliveryPic, title: 'Last-Mile Delivery',
    outcome: 'Delivery windows your customers trust',
    text: "Route-optimized final delivery from our distribution network to your customer's door. We reduce failed deliveries and handle rescheduling — so you don't have to.",
    to: '/services/last-mile-delivery',
  },
  {
    img: planPic, title: 'Custom Logistics Plans',
    outcome: 'Built around your operations, not ours',
    text: 'Intake call → lane audit → tailored proposal. We identify consolidation opportunities, negotiate carrier rates, and design a supply chain playbook matched to your volume and timeline.',
    to: '/services/custom-logistics',
  },
];

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

export const OffersPage = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const targets = sectionRef.current?.querySelectorAll<HTMLElement>('.rn-reveal');
    if (!targets) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    targets.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef}>
      <div className="cs-container">

        <div className="cs-content rn-reveal">
          <span className="cs-topper">Our Services</span>
          <h2 className="cs-title">Freight, Warehousing, and Last Mile — One Team, One Contact</h2>
          <p className="cs-text">
            Freight, warehousing, and last-mile delivery managed by a single team.
            No handoffs. One point of contact. Complete visibility at every stage.
          </p>
        </div>

        {/* Featured card */}
        <div className="cs-featured rn-reveal">
          <div className="cs-featured__img">
            <img loading="lazy" decoding="async" src={FEATURED.img} alt={FEATURED.title} />
            <span className="cs-featured__badge">{FEATURED.label}</span>
          </div>
          <div className="cs-featured__body">
            <span className="cs-featured__outcome">{FEATURED.outcome}</span>
            <h3 className="cs-featured__title">{FEATURED.title}</h3>
            <p className="cs-featured__text">{FEATURED.text}</p>
            <ul className="cs-featured__bullets">
              {FEATURED.bullets.map(b => (
                <li key={b}><CheckIcon />{b}</li>
              ))}
            </ul>
            <Link to="/quote" className="cs-button-solid">Request Freight Quote <ArrowIcon /></Link>
          </div>
        </div>

        {/* Secondary cards */}
        <ul className="cs-card-group">
          {SERVICES.map(({ img, title, outcome, text, to }, i) => (
            <li key={title} className={`cs-item rn-reveal rn-reveal-delay-${(i % 2) + 1}`}>
              <Link to={to} className="cs-link">
                <div className="cs-picture">
                  <img loading="lazy" decoding="async" src={img} alt={title} width="413" height="240" />
                </div>
                <div className="cs-item-content">
                  <span className="cs-item-outcome">{outcome}</span>
                  <h3 className="cs-h3">{title}</h3>
                  <p className="cs-item-text">{text}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>

      </div>
    </section>
  );
};
